<template>
  <div class="irp-master-tab" data-testid="irp-master-tab">
    <template v-if="!hasError">
      <div class="irp-master-tab__header">
        <h3 class="irp-master-tab__heading">{{ detailsHeading }}</h3>
        <IRPExpandToggle
          v-if="ownList?.hasHiddenFields"
          v-model:is-expanded="isOwnExpanded"
          :cta="IRP_CTAS.OWN_DATA"
          data-testid="irp-master-tab-expand-toggle"
        />
      </div>
      <div :class="{ 'irp-master-tab__card': hasOwnDetails }">
        <div
          v-if="isInitialLoading"
          class="irp-master-tab__skeleton"
          data-testid="irp-master-tab-skeleton"
        >
          <BittsBone v-for="row in SKELETON_ROW_COUNT" :key="row" type="copy" />
        </div>
        <IRPRecordDetails
          v-else
          ref="ownList"
          :fields="ownFields"
          :account-owners="ownAccountOwners"
          :is-own-data="true"
          :empty-message="emptyMessage"
        />
      </div>
    </template>

    <IRPPartnerDataSection section="master">
      <template #default="{ partner }">
        <div class="irp-master-tab__card">
          <IRPRecordDetails
            :fields="partner.fields"
            :account-owners="partnerAccountOwners(partner)"
          />
        </div>
      </template>
    </IRPPartnerDataSection>
  </div>
</template>

<script setup lang="ts">
import { BittsBone } from '@crossbeam/bitts';
import { IRP_CTAS } from '@crossbeam/itly';

import { computed, useTemplateRef } from 'vue';

import { useIndividualRecordStore } from '@/stores';
import type {
  RecordCollectionType,
  RecordFieldType,
  RecordPartnerType,
} from '@/types/individual_record';

import { IRP_DETAILS_HEADINGS } from './irp';
import IRPExpandToggle from './IRPExpandToggle.vue';
import IRPPartnerDataSection from './IRPPartnerDataSection.vue';
import IRPRecordDetails from './IRPRecordDetails.vue';

const SKELETON_ROW_COUNT = 2;

const EMPTY_MESSAGES = {
  account: 'No account details on this record',
  lead: 'No lead details on this record',
} as const;

const individualRecordStore = useIndividualRecordStore();

const mdmType = computed(() => individualRecordStore.mdmType ?? 'account');

const detailsHeading = computed(() => IRP_DETAILS_HEADINGS[mdmType.value]);
const emptyMessage = computed(() => EMPTY_MESSAGES[mdmType.value]);

const isInitialLoading = computed(
  () =>
    !!individualRecordStore.master?.isLoading &&
    !individualRecordStore.master?.hasLoaded,
);

const hasError = computed(() => !!individualRecordStore.master?.error);

const ownFields = computed<RecordFieldType[]>(
  () => individualRecordStore.master?.own?.fields ?? [],
);

const ownAccountOwners = computed(
  () =>
    individualRecordStore.master?.own?.account_owners as
      | RecordCollectionType
      | undefined,
);

const hasOwnDetails = computed(
  () => isInitialLoading.value || !!ownFields.value.length,
);

const ownList =
  useTemplateRef<InstanceType<typeof IRPRecordDetails>>('ownList');

const isOwnExpanded = computed({
  get: () => !!ownList.value?.isEveryRowOpen,
  set: (isExpanded: boolean) => ownList.value?.setAllRowsOpen(isExpanded),
});

individualRecordStore.fetchOwnSection('contacts');

function partnerAccountOwners(partner: RecordPartnerType<'master'>) {
  return partner.account_owners as RecordCollectionType | undefined;
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-master-tab {
  @apply flex flex-col gap-16;
}

.irp-master-tab__header {
  @apply flex items-center justify-between gap-8;
}

.irp-master-tab__heading {
  @apply text-base font-bold text-neutral-text-strong;
}

.irp-master-tab__card {
  @apply rounded-16 border border-neutral-border bg-white py-24 pl-24 pr-16 shadow-component;
}

.irp-master-tab__skeleton {
  @apply flex flex-col gap-16;
}
</style>
